import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import { ExpandMore, Notes } from "@mui/icons-material";
import {
  createNote,
  deleteNote,
  fetchNotes,
  updateNote,
} from "../../reducers/PerNotesReducer";
import PATAddButton from "../Buttons/PATAddButton";
import CustomTable from "../CustomTable";
import PATEditButton from "../Buttons/PATEditButton";
import PATDeleteButton from "../Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import PerNoteDialog from "./PerNoteDialog";
import { formatDateToMonthDateYear } from "../../utils/functions";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import { perNotesOptions } from "../../utils/enums";

const tableHeaders = ["Note", "Created At", "Updated At", "Actions"];

const PerNotesTable = ({
  noteFor,
  entityId,
  perNotes,
  theme,
  allExpanded = false,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [openPerNotesDialog, setOpenPerNotesDialog] = useState(false);
  const [editingPerNote, setEditingPerNote] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [perNoteToDelete, setPerNoteToDelete] = useState(null);
  const [expanded, setExpanded] = useState(allExpanded);

  const getNoteTabPermissions = () => {
    if (noteFor === perNotesOptions.Applicants) {
      return getAccessPermission("applicants", "Notes Tab", "any");
    }
    if (noteFor === perNotesOptions.Employees) {
      return getAccessPermission("employees", "Notes Tab", "any");
    }
    return null;
  };

  const noteTabPermission = getNoteTabPermissions();
  const canAdd = noteTabPermission?.Create ?? true;
  const canEdit = noteTabPermission?.Edit ?? true;
  const canDelete = noteTabPermission?.Delete ?? true;

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  useEffect(() => {
    if (noteFor && entityId) {
      dispatch(fetchNotes(noteFor, entityId));
    }
  }, [noteFor, entityId, dispatch]);

  const handleOpenAddPerNote = (e) => {
    e.stopPropagation();
    setEditingPerNote(null);
    setOpenPerNotesDialog(true);
  };

  const handleOpenPerNote = (e, note) => {
    e.stopPropagation();
    setEditingPerNote(note);
    setOpenPerNotesDialog(true);
  };

  const handleSavePerNote = (data) => {
    if (editingPerNote) {
      dispatch(
        updateNote(editingPerNote.id, {
          ...data,
          noteFor: noteFor,
          entityId: entityId,
        })
      ).then(() => dispatch(fetchNotes(noteFor, entityId)));
    } else {
      dispatch(
        createNote({
          ...data,
          noteFor: noteFor,
          entityId: entityId,
        })
      ).then(() => dispatch(fetchNotes(noteFor, entityId)));
    }
  };

  const handleOpenDeleteDialog = (e, ecId) => {
    e.stopPropagation();
    setPerNoteToDelete(ecId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (perNoteToDelete) {
      dispatch(
        deleteNote(
          perNoteToDelete,
          userData?.user?.ptsUserID,
          noteFor,
          entityId
        )
      ).then(() => dispatch(fetchNotes(noteFor, entityId)));
    }
    setOpenDeleteDialog(false);
    setPerNoteToDelete(null);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <Notes sx={{ mr: 1 }} />
              <span>Notes</span>
            </Box>
            <Box sx={{ mr: 2 }}>
              {!readOnly && canAdd && (
                <PATAddButton
                  tooltipText="Add Note"
                  onClick={handleOpenAddPerNote}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {perNotes?.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={perNotes}
              renderRow={(note, index) => (
                <TableRow key={index}>
                  <TableCell>{note.note}</TableCell>
                  <TableCell>
                    {formatDateToMonthDateYear(note.createdAt)}
                  </TableCell>
                  <TableCell>
                    {formatDateToMonthDateYear(note.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {!readOnly && canEdit && (
                        <PATEditButton
                          tooltipText="Edit Note"
                          onClick={(e) => handleOpenPerNote(e, note)}
                          size="20px"
                        />
                      )}
                      {!readOnly && canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Note"
                          onClick={(e) => handleOpenDeleteDialog(e, note.id)}
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Notes Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <PerNoteDialog
        open={openPerNotesDialog}
        onClose={() => setOpenPerNotesDialog(false)}
        onSave={handleSavePerNote}
        initialData={editingPerNote}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setPerNoteToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
      />
    </>
  );
};

export default PerNotesTable;
